<!-- eslint-disable vue/html-self-closing -->
<template>
  <section class="page-contributors">
    <div class="page-contributors__header">
      <h4>{{ $t('contributors.analytics') }}</h4>
    </div>
    <div
      v-if="contributors?.contributorAnalyticsAvailable"
      class="page-contributors__body mt-3"
    >
      <div class="page-contributors__body-filters">
        <project-branch-selector
          v-if="selectedBranch"
          v-model="selectedBranch"
          :selected="selectedBranch"
          :alias="alias"
          :project-model="projectAlias"
          style="max-width: 180px; min-width: 130px"
          @input="changeBranch"
        />
        <div>
          <div class="page-contributors__body-filters-dates">
            <input
              id="afterDate"
              v-model="selectedAfterDate"
              type="date"
              name="afterDate"
              :min="$moment(selectedBeforeDate).subtract(365, 'days').format('YYYY-MM-DD')"
              :max="selectedBeforeDate"
              class="form-control"
            />
            <span>—</span>
            <input
              id="beforeDate"
              v-model="selectedBeforeDate"
              type="date"
              name="beforeDate"
              class="form-control"
              :max="todayDate"
            />
            <button
              class="btn btn-sm btn-dark"
              :disabled="!datesHaveChanges || datesHaveError || datesAreMoreThenYear"
              @click="changeDates"
            >
              <i class="gf-icon gf-icon-search"></i>
            </button>
          </div>
          <div
            class="text-danger"
            v-if="datesHaveError || datesAreMoreThenYear"
          >
            <small v-if="datesHaveError">Начальная дата больше конечной</small>
            <small v-if="datesAreMoreThenYear">Промежуток больше года</small>
          </div>
        </div>
      </div>
      <div
        v-if="contributors?.repositoryBlock"
        class="page-contributors__body-statistics"
      >
        <div class="page-contributors__body-statistics-branch mb-4">
          <div class="mb-3">
            <h5 class="mb-1">
              {{ $t('commits.branch') }}
              <code>{{ branch }}</code>
            </h5>
            <div class="text-muted mb-2">
              {{ $t('commits.excluding') }}
              {{ $t('commits.limit', [contributors?.maxCommitCount]) }}
            </div>
            <span>{{ $t('commits.text', [contributors?.commitCount]) }}</span>
          </div>
          <gf-commit-line-graph
            v-if="contributors?.repositoryBlock && afterDate && beforeDate"
            :commit-data="contributors?.repositoryBlock"
            :max-commits-number="contributors?.repositoryBlock?.maxCommits || 1"
          />
        </div>
        <div
          v-if="contributors?.usersBlockList"
          class="page-contributors__body-statistics-users row"
        >
          <div
            v-for="(userBlock, index) in contributors.usersBlockList"
            :key="index"
            class="page-contributors__body-statistics-users-item col-16 col-md-6 mb-3"
          >
            <div class="page-contributors__body-statistics-users-item-user mb-2">
              <img
                class="rounded-circle mr-1"
                height="35px"
                width="35px"
                :src="userBlock.user ? userBlock.user?.avatar : '/static/image/avatar.jpg'"
                loading="lazy"
              />
              <div class="page-contributors__body-statistics-users-item-user-info">
                <h5 class="d-flex align-items-end mb-0">
                  <a
                    v-if="userBlock.user"
                    :href="`/user/${userBlock.user?.username}`"
                    class="text-dark text-truncate"
                  >
                    {{ userBlock.user?.fullName }}
                  </a>
                  <a
                    v-else
                    :href="`mailto:${userBlock?.email}`"
                    class="text-dark text-truncate"
                  >
                    {{ userBlock?.username }}
                  </a>
                </h5>
                <span>{{ $t('commits.text', [userBlock?.commitsCount]) }}</span>
              </div>
            </div>
            <gf-commit-line-graph
              :commit-data="userBlock"
              :max-commits-number="maxUserCommitNumber"
            />
          </div>
        </div>
      </div>
      <div
        v-else-if="contributors?.branchName"
        class="page-contributors__body-statistics"
      >
        <div class="page-contributors__body-statistics-empty p-3">
          <h6 class="mb-0">Нет коммитов по указанным параметрам</h6>
        </div>
      </div>
      <div
        v-else-if="maxUserCommitNumber === 0"
        class="my-2 w-100"
      >
        <span class="card w-100 p-3 text-center bg-light">Нет коммитов</span>
      </div>
      <div
        v-else
        class="page-contributors__body-statistics --loading"
      >
        <packman-loader />
      </div>
    </div>

    <div
      v-else
      class="my-2 w-100 mt-3"
    >
      <span class="card w-100 p-3 text-center bg-light">Нет коммитов</span>
    </div>
  </section>
</template>

<script>
// eslint-disable-next-line max-len
import projectBranchSelector from '@/shared-modules/shared-components/shared/project-branch-selector';
import InsightsService from '@/shared-modules/modules/insights/insights.service';
import GfCommitLineGraph from '@/shared-modules/modules/insights/components/GfCommitLineGraph';
import packmanLoader from '@/shared-modules/shared-components/shared/packman-loader';
import InsightsMessages from '@/shared-modules/modules/insights/insights.messages';

export default {
  name: 'PageContributors',
  components: { projectBranchSelector, GfCommitLineGraph, packmanLoader },
  data() {
    return {
      selectedBranch: null,
      selectedAfterDate: null,
      selectedBeforeDate: null,
      contributors: null
    };
  },
  computed: {
    alias() {
      return this.$route?.params?.alias;
    },
    projectAlias() {
      return this.$route?.params?.projectAlias;
    },
    afterDate() {
      return this.$route?.query?.afterDate;
    },
    beforeDate() {
      return this.$route?.query?.beforeDate;
    },
    branch() {
      return this.$route.query?.branchName;
    },
    todayDate() {
      return this.$moment().format('YYYY-MM-DD');
    },
    datesHaveChanges() {
      return (
        this.selectedAfterDate !== this.afterDate || this.selectedBeforeDate !== this.beforeDate
      );
    },
    datesHaveError() {
      return this.$moment(this.selectedAfterDate).isAfter(this.selectedBeforeDate);
    },
    datesAreMoreThenYear() {
      return this.$moment(this.selectedBeforeDate).diff(this.selectedAfterDate, 'days') > 365;
    },
    maxUserCommitNumber() {
      let maxCommitNumber = 0;
      this.contributors?.usersBlockList?.forEach((userBlock) => {
        if (userBlock?.maxCommits > maxCommitNumber) maxCommitNumber = userBlock?.maxCommits;
      });
      return maxCommitNumber;
    }
  },
  mounted() {
    if (this.$route.query?.branchName) {
      this.selectedBranch = this.branch;
    }
    if (this.afterDate && this.beforeDate) {
      this.selectedBeforeDate = this.beforeDate;
      this.selectedAfterDate = this.afterDate;
    } else {
      this.selectedBeforeDate = this.todayDate;
      this.selectedAfterDate = this.$moment().subtract(60, 'days').format('YYYY-MM-DD');
      this.$router.push({
        query: {
          ...this.$route.query,
          beforeDate: this.selectedBeforeDate,
          afterDate: this.selectedAfterDate
        }
      });
    }
    this.getContributorsAnalytics();
  },
  methods: {
    getContributorsAnalytics() {
      this.contributors = null;
      InsightsService.getContributorsAnalytics(
        this.alias,
        this.projectAlias,
        this.$route.query
      ).then((res) => {
        this.contributors = res;
        if (this.branch !== res?.branchName) {
          this.selectedBranch = res?.branchName;
          this.$router.push({
            query: {
              ...this.$route.query,
              branchName: res?.branchName
            }
          });
        }
      });
    },
    changeBranch() {
      if (this.branch !== (this.selectedBranch?.title || this.selectedBranch)) {
        this.$router.push({
          query: {
            ...this.$route.query,
            branchName: this.selectedBranch?.title
          }
        });
        this.getContributorsAnalytics();
      }
    },
    changeDates() {
      if (this.datesHaveChanges) {
        this.$router.push({
          query: {
            ...this.$route.query,
            afterDate: this.selectedAfterDate,
            beforeDate: this.selectedBeforeDate
          }
        });
        this.getContributorsAnalytics();
      }
    }
  },
  i18n: {
    messages: InsightsMessages
  }
};
</script>

<style lang="scss">
$this: '.page-contributors';

#{$this} {
  #{$this}__body {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &-filters {
      display: flex;
      justify-content: space-between;
      gap: 12px;

      @media screen and (max-width: 767px) {
        justify-content: flex-start;
        flex-direction: column;
      }

      &-dates {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    &-statistics {
      &-users {
        &-item {
          &-user {
            display: flex;
            gap: 4px;
            align-items: flex-start;

            &-info {
              display: flex;
              flex-direction: column;
            }
          }
        }
      }
      &-empty {
        width: 100%;
        background-color: var(--light);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      &.--loading {
        position: relative;
        height: 100px;
      }
    }
  }
}
</style>
